import { useState, useEffect } from 'react';
import Layout from '../../components/global/layout';
import { useSelector } from 'react-redux';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

const plans = [
  {
    id: 'tier_a',
    name: 'Tier A',
    label: '15 or fewer',
    maxAttendance: 15,
    monthlyPrice: 50,
    profilesIncluded: 300,
  },
  {
    id: 'tier_b',
    name: 'Tier B',
    label: '16 to 25',
    maxAttendance: 25,
    monthlyPrice: 70,
    profilesIncluded: 400,
  },
  {
    id: 'tier_c',
    name: 'Tier C',
    label: '26 to 50',
    maxAttendance: 50,
    monthlyPrice: 100,
    profilesIncluded: 500,
  },
  {
    id: 'tier_d',
    name: 'Tier D',
    label: '51 to 75',
    maxAttendance: 75,
    monthlyPrice: 130,
    profilesIncluded: 600,
  },
];

const ADDON_BLOCK_SIZE = 500;
const ADDON_BLOCK_PRICE_MONTHLY = 50;
const QUARTER_DAYS = 90;

function getNextQuarterStart() {
  const now = new Date();
  const year = now.getFullYear();
  const quarters = [
    new Date(year, 0, 1), new Date(year, 3, 1),
    new Date(year, 6, 1), new Date(year, 9, 1),
    new Date(year + 1, 0, 1),
  ];
  for (const q of quarters) { if (q > now) return q; }
  return quarters[quarters.length - 1];
}

function getDaysUntilNextQuarter() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const next = getNextQuarterStart();
  return Math.ceil((next - now) / (1000 * 60 * 60 * 24));
}

function getQuarterLabel(date) {
  const m = date.getMonth();
  const labels = { 0: 'Jan 1', 3: 'Apr 1', 6: 'Jul 1', 9: 'Oct 1' };
  return labels[m] || `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`;
}

const Subscription = () => {
  const user = useSelector((state) => state.user);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [memberCount, setMemberCount] = useState(null);
  const [profileAddOnBlocks, setProfileAddOnBlocks] = useState(0);
  const [searchParams] = useSearchParams();
  const groupId = user?.lastUsedGroupId?._id || user?.lastUsedGroupId || user?.groupId?._id || user?.groupId;

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription activated successfully!');
    }
    if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout was canceled.');
    }
  }, [searchParams]);

  const fetchGroupStatus = () => {
    if (!groupId) return;
    ApiClient.get('api/group/detail', { id: groupId }).then((res) => {
      if (res.success && res.data) {
        const sub = res.data.subscription || {};
        if (sub.tier && sub.endDate) {
          const matchedPlan = plans.find((p) => p.id === sub.tier);
          setCurrentPlan({
            planId: sub.tier,
            planName: matchedPlan ? matchedPlan.name : sub.tier,
            label: matchedPlan ? matchedPlan.label : '',
            maxAttendance: sub.maxAttendance || (matchedPlan ? matchedPlan.maxAttendance : 0),
            profilesIncluded: sub.profilesIncluded || (matchedPlan ? matchedPlan.profilesIncluded : 0),
            profileAddOnBlocks: sub.profileAddOnBlocks || 0,
            maxMembers: sub.maxMembers || 0,
            billingInterval: sub.billingInterval || 'month',
          });
        } else if (sub.maxMembers > 0 && sub.endDate) {
          setCurrentPlan({
            planId: null,
            planName: 'Legacy',
            maxMembers: sub.maxMembers,
          });
        } else {
          setCurrentPlan(null);
        }
      }
    });
    ApiClient.get('api/members/list', { groupId, count: 1, page: 1 }).then((res) => {
      if (res.success && res.total !== undefined) {
        setMemberCount(res.total);
      }
    });
  };

  useEffect(() => {
    if (user && user.loggedIn) {
      fetchGroupStatus();
    }
  }, [user]);

  const handleSelectPlan = (plan) => {
    loader(true);
    ApiClient.post('api/subscription/checkout', {
      planId: plan.id,
      userId: user._id || user.id,
      groupId: groupId,
      profileAddOnBlocks: profileAddOnBlocks,
    }).then((res) => {
      loader(false);
      if (res.success) {
        if (res.data?.upgraded) {
          toast.success(res.message || 'Plan updated!');
          fetchGroupStatus();
        } else if (res.data?.url) {
          window.location.href = res.data.url;
        }
      } else {
        toast.error(res.message || 'Failed to start checkout');
      }
    }).catch(() => {
      loader(false);
      toast.error('Something went wrong');
    });
  };

  const handleBillingPortal = () => {
    loader(true);
    ApiClient.post('api/subscription/billing-portal', {
      userId: user._id || user.id,
    }).then((res) => {
      loader(false);
      if (res.success && res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(res.message || 'Unable to open billing portal');
      }
    }).catch(() => {
      loader(false);
      toast.error('Something went wrong');
    });
  };

  const handleCancel = () => {
    Swal.fire({
      title: 'Cancel Subscription?',
      text: 'Your access will continue until the end of your current billing period. No refunds for remaining time.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'Keep subscription',
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true);
        ApiClient.post('api/subscription/cancel', {
          userId: user._id || user.id,
        }).then((res) => {
          loader(false);
          if (res.success) {
            toast.success(res.message || 'Subscription cancelled');
            setCurrentPlan(null);
            fetchGroupStatus();
          } else {
            toast.error(res.message || 'Failed to cancel');
          }
        }).catch(() => {
          loader(false);
          toast.error('Something went wrong');
        });
      }
    });
  };

  const daysToNext = getDaysUntilNextQuarter();
  const nextQuarter = getNextQuarterStart();
  const nextQuarterLabel = getQuarterLabel(nextQuarter);

  const getQuarterlyPrice = (plan) => plan.monthlyPrice * 3;

  const getAddonQuarterlyPrice = () => ADDON_BLOCK_PRICE_MONTHLY * 3;

  const getProratedTotal = (plan) => {
    const quarterlyPlan = getQuarterlyPrice(plan);
    const quarterlyAddon = getAddonQuarterlyPrice() * profileAddOnBlocks;
    const totalQuarterly = quarterlyPlan + quarterlyAddon;
    if (daysToNext <= 3) return totalQuarterly;
    return Math.round((daysToNext / QUARTER_DAYS) * totalQuarterly * 100) / 100;
  };

  return (
    <Layout>
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">Subscription Plans</h2>
        <p className="text-gray-600 mb-1">
          Choose the tier matching your group's expected attendance per meeting.
        </p>
        <p className="text-gray-500 text-sm mb-6">
          Founding/launch pricing for our first customers*
        </p>

        {memberCount !== null && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">
              Your group currently has <span className="font-bold">{memberCount}</span> member profile{memberCount !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {currentPlan && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-green-800 font-medium">
                Current Plan: <span className="font-bold">{currentPlan.planName}</span>
                {currentPlan.label && <span> – {currentPlan.label} expected attendance</span>}
              </p>
              {currentPlan.profilesIncluded > 0 && (
                <p className="text-green-700 text-sm mt-1">
                  {currentPlan.profilesIncluded.toLocaleString()} profiles included
                  {currentPlan.profileAddOnBlocks > 0 && ` + ${(currentPlan.profileAddOnBlocks * ADDON_BLOCK_SIZE).toLocaleString()} add-on profiles`}
                  {' '}({currentPlan.maxMembers?.toLocaleString()} total)
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBillingPortal}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 font-medium"
              >
                Manage Billing
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50 font-medium"
              >
                Cancel Plan
              </button>
            </div>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
          {plans.map((plan) => {
            const isCurrent = currentPlan?.planId === plan.id;
            const quarterlyPrice = getQuarterlyPrice(plan);
            const proratedTotal = getProratedTotal(plan);
            const skipProration = daysToNext <= 3;
            return (
              <div
                key={plan.id}
                className={`relative p-5 rounded-lg border-2 transition-all ${
                  isCurrent
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-200 hover:border-[#5577FF]/50 hover:shadow-md'
                }`}
              >
                <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{plan.label} attendees</p>
                <div className="mb-1">
                  <span className="text-3xl font-bold">${quarterlyPrice}</span>
                  <span className="text-gray-500">/qtr</span>
                </div>
                <p className="text-sm text-gray-400 mb-3">${plan.monthlyPrice}/mo equivalent</p>
                <ul className="space-y-2 mb-5 text-left">
                  <li className="flex items-start gap-1">
                    <span className="text-[#22963e] text-lg font-bold mt-0.5">&#10003;</span>
                    <span className="text-gray-600 text-sm">{plan.profilesIncluded} member profiles included</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span className="text-[#22963e] text-lg font-bold mt-0.5">&#10003;</span>
                    <span className="text-gray-600 text-sm">All features included</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span className="text-[#22963e] text-lg font-bold mt-0.5">&#10003;</span>
                    <span className="text-gray-600 text-sm">Quarterly tier evaluation</span>
                  </li>
                </ul>
                {!isCurrent && !currentPlan && (
                  <div className="mb-3 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
                    {skipProration ? (
                      <p>Today's charge: <strong>${quarterlyPrice + (getAddonQuarterlyPrice() * profileAddOnBlocks)}</strong> for the full quarter starting {nextQuarterLabel}.</p>
                    ) : (
                      <>
                        <p>Today's charge: <strong>${proratedTotal.toFixed(2)}</strong> (prorated for {daysToNext} days until {nextQuarterLabel}).</p>
                        <p className="mt-1">Then ${quarterlyPrice + (getAddonQuarterlyPrice() * profileAddOnBlocks)}/qtr starting {nextQuarterLabel}.</p>
                      </>
                    )}
                  </div>
                )}
                {isCurrent ? (
                  <button
                    disabled
                    className="w-full py-2 rounded-md bg-green-100 text-green-700 font-medium text-sm"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    className="w-full py-2 rounded-md font-medium text-sm transition-all bg-gray-300 text-gray-700 hover:bg-[#5577FF] hover:text-white"
                  >
                    {currentPlan ? 'Switch Plan' : 'Select Plan'}
                  </button>
                )}
              </div>
            );
          })}

          {/* Tier E - Contact Us */}
          <div className="relative p-5 rounded-lg border-2 border-gray-200 hover:border-[#5577FF]/50 hover:shadow-md transition-all">
            <h3 className="text-lg font-bold mb-1">Tier E</h3>
            <p className="text-sm text-gray-500 mb-3">75+ attendees</p>
            <div className="mb-1">
              <span className="text-2xl font-bold text-gray-600">Custom</span>
            </div>
            <p className="text-sm text-gray-400 mb-3">Contact us for a quote</p>
            <ul className="space-y-2 mb-5 text-left">
              <li className="flex items-start gap-1">
                <span className="text-[#22963e] text-lg font-bold mt-0.5">&#10003;</span>
                <span className="text-gray-600 text-sm">Custom profile limit</span>
              </li>
              <li className="flex items-start gap-1">
                <span className="text-[#22963e] text-lg font-bold mt-0.5">&#10003;</span>
                <span className="text-gray-600 text-sm">All features included</span>
              </li>
              <li className="flex items-start gap-1">
                <span className="text-[#22963e] text-lg font-bold mt-0.5">&#10003;</span>
                <span className="text-gray-600 text-sm">Quarterly tier evaluation</span>
              </li>
            </ul>
            <a
              href="mailto:team@skillsnetpro.com"
              className="block w-full py-2 rounded-md font-medium text-sm text-center transition-all bg-gray-300 text-gray-700 hover:bg-[#5577FF] hover:text-white"
            >
              Contact Us
            </a>
          </div>
        </div>

        {/* Profile Add-on */}
        <div className="mb-8 p-5 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Member Profile Add-on</h3>
          <p className="text-gray-600 text-sm mb-4">
            Need more member profiles beyond what's included in your tier? Add extra profiles at $0.10 per profile, sold in blocks of 500 ($50/month per block). Blocks are stackable — for example, 2 blocks = 1,000 extra profiles for $100/month.
          </p>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Extra profile blocks:</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setProfileAddOnBlocks(Math.max(0, profileAddOnBlocks - 1))}
                className="w-8 h-8 rounded border border-gray-300 flex items-center justify-center text-lg font-bold text-gray-600 hover:bg-gray-100"
                disabled={profileAddOnBlocks === 0}
              >
                −
              </button>
              <span className="w-8 text-center font-semibold">{profileAddOnBlocks}</span>
              <button
                onClick={() => setProfileAddOnBlocks(profileAddOnBlocks + 1)}
                className="w-8 h-8 rounded border border-gray-300 flex items-center justify-center text-lg font-bold text-gray-600 hover:bg-gray-100"
              >
                +
              </button>
            </div>
            {profileAddOnBlocks > 0 && (
              <span className="text-sm text-gray-600">
                +{(profileAddOnBlocks * ADDON_BLOCK_SIZE).toLocaleString()} profiles (+${getAddonQuarterlyPrice() * profileAddOnBlocks}/qtr)
              </span>
            )}
          </div>
        </div>

        {/* How billing works */}
        <div className="p-5 bg-white border border-gray-200 rounded-lg">
          <h3 className="text-lg font-semibold mb-3">How Billing Works</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><strong>Expected Attendance Tier:</strong> Pick the tier matching how many people typically attend your group meeting. This is what you're billed on.</li>
            <li><strong>Member Profiles Included:</strong> A separate, more generous cap on your roster (active members, guests, prospects). Not a billing trigger.</li>
            <li><strong>Quarterly Billing:</strong> All plans are billed quarterly, aligned to fixed calendar quarters (Jan 1, Apr 1, Jul 1, Oct 1). If you sign up mid-quarter, you pay a prorated amount upfront: (days until next quarter / 90) x quarterly tier price. For example, signing up Feb 15 with Tier A ($150/qtr): there are 45 days until Apr 1, so you pay (45 / 90) x $150 = $75 today. Then on Apr 1, you pay the full $150 for the next quarter (Apr 1 – Jul 1).</li>
            <li><strong>Tier Evaluation:</strong> Every quarter, average attendance over the past 3 months is calculated. If it exceeds your current tier, you move up. Tiers don't auto step-down — contact us to request a lower tier.</li>
            <li><strong>First Quarter:</strong> You pay the tier you selected. Starting the next evaluation, your tier is based on your actual average attendance.</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default Subscription;
